/**
 * expandable-text
 *
 * Collapses long text via CSS max-height + gradient fade.
 * No text manipulation — só toggle de atributo.
 *
 * Attributes:
 *   max-chars   {number}  Approximate char limit → converted to max-height. Default: 300.
 *   label-more  {string}  Button label when collapsed. Default: "ver mais".
 *   label-less  {string}  Button label when expanded.  Default: "ver menos".
 *
 * CSS custom properties (set on the element or a parent):
 *   --expandable-bg   Background color for the gradient fade. Default: #fff.
 */
class ExpandableText extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const maxChars = parseInt(this.getAttribute('max-chars') ?? '300', 10);
    const labelMore = this.getAttribute('label-more') ?? 'ver mais';
    const labelLess = this.getAttribute('label-less') ?? 'ver menos';
    const totalChars = (this.textContent ?? '').trim().length;

    if (totalChars <= maxChars) return;

    // Translate chars to an approximate max-height (~60 chars/line, 1.6 line-height)
    const lines = Math.max(2, Math.ceil(maxChars / 60));
    this.style.setProperty('--expandable-max-height', `${lines * 1.6}em`);
    this.setAttribute('collapsible', '');

    // Toggle button
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'expandable-text__toggle';
    toggle.textContent = labelMore;
    this.appendChild(toggle);

    toggle.addEventListener(
      'click',
      () => {
        const expanded = this.hasAttribute('expanded');
        this.toggleAttribute('expanded', !expanded);
        toggle.textContent = expanded ? labelMore : labelLess;
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }
}

if (!customElements.get('expandable-text')) {
  customElements.define('expandable-text', ExpandableText);
}